/* prettier-ignore */

import { effect, inject, Injectable, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';

/**
 * HRS-3134: a dónde llevar al visitante cuando acaba de iniciar sesión.
 *
 * Hace falta porque la web no tiene pantalla de login: es un modal que se abre
 * encima de lo que estés viendo. El guard de `/my-account/**` navegaba a
 * `/account/login`, una ruta que no existe, así que un enlace directo a los
 * favoritos —como el del correo del registro— acababa en una pantalla vacía.
 *
 * El destino se recuerda aquí y el efecto lo consume en cuanto hay sesión, sea el
 * modal de Ionic o el diálogo de Material el que la haya abierto.
 */
@Injectable({ providedIn: 'root' })
export class PostLoginRedirect {
  private _router = inject(Router);
  private _authService = inject(AuthService);

  /**
   * Ruta interna pendiente; se olvida en cuanto se usa. HRS-3482: se guarda
   * también en sessionStorage porque el login social (Google) sale de la app en
   * la misma pestaña y vuelve recargándola; solo en memoria se perdía. Caduca a
   * los 10 minutos (memoria y storage): en un dispositivo compartido, un destino
   * olvidado (p.ej. `?claim=` de otro comprador) no debe heredarlo quien entre después.
   */
  private _pending: PendingRedirect | null = readStoredPending();
  private _hadUser = false;

  constructor() {
    effect(() => {
      const user = this._authService.user();

      // Cerrar sesión descarta el destino (sin inyectar esto en AuthService: ciclo).
      if (!user && this._hadUser) this.forget();
      this._hadUser = !!user;

      if (!user || !this._pending) {
        return;
      }

      const pending = this._pending;
      this.forget();
      if (isExpired(pending)) return;

      // Fuera del efecto: navegar dispara guards que vuelven a leer signals.
      untracked(() => this._router.navigateByUrl(pending.url));
    });
  }

  public remember(url: string): void {
    if (!isInternalPath(url)) {
      this.forget();
      return;
    }
    this._pending = { url, savedAt: Date.now() };
    storePending(this._pending);
  }

  public forget(): void {
    this._pending = null;
    storePending(null);
  }
}

interface PendingRedirect {
  url: string;
  savedAt: number;
}

const PENDING_STORAGE_KEY = 'postLoginRedirect';
const PENDING_TTL_MS = 10 * 60 * 1000;

function isInternalPath(url: unknown): url is string {
  return typeof url === 'string' && url.startsWith('/') && !url.startsWith('//');
}

function isExpired(pending: PendingRedirect): boolean {
  const age = Date.now() - pending.savedAt;
  return !(age >= 0 && age <= PENDING_TTL_MS);
}

function readStoredPending(): PendingRedirect | null {
  try {
    const raw = sessionStorage.getItem(PENDING_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || !isInternalPath(parsed.url) || typeof parsed.savedAt !== 'number') return null;
    return isExpired(parsed) ? null : { url: parsed.url, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

function storePending(pending: PendingRedirect | null): void {
  try {
    if (pending) sessionStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(pending));
    else sessionStorage.removeItem(PENDING_STORAGE_KEY);
  } catch {
    // sessionStorage no disponible (modo privado, bloqueado): queda solo en memoria.
  }
}
